import { createRoute, redirect } from "@tanstack/react-router";
import {
  Add01Icon,
  Delete02Icon,
  Edit02Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/toast";
import { api, ApiError, type UserGroup } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { rootRoute } from "./root-route";

export const userGroupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/user-groups",
  beforeLoad: async () => {
    try {
      await api.me();
    } catch {
      throw redirect({
        to: "/login",
        search: { returnTo: "/user-groups", error: undefined },
      });
    }
  },
  component: UserGroupsPage,
});

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString("zh-CN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

/** Split pasted UID text (comma / whitespace / newline). */
function parseUidTextClient(text: string): {
  ok: true;
  uids: number[];
} | {
  ok: false;
  badToken: string;
} {
  const tokens = text
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter(Boolean);
  if (tokens.length === 0) return { ok: true, uids: [] };
  const out: number[] = [];
  const seen = new Set<number>();
  for (const token of tokens) {
    if (!/^\d+$/.test(token)) return { ok: false, badToken: token };
    const n = Number(token);
    if (!Number.isSafeInteger(n) || n < 1) return { ok: false, badToken: token };
    if (seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  out.sort((a, b) => a - b);
  return { ok: true, uids: out };
}

function GroupsTableSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="flex flex-col gap-0">
        <div className="flex gap-4 border-b border-border px-4 py-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-20" />
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-4 border-b border-border px-4 py-3 last:border-0"
          >
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-8 w-24" />
          </div>
        ))}
      </div>
    </div>
  );
}

function UserGroupsPage() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [uidsText, setUidsText] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [editGroup, setEditGroup] = useState<UserGroup | null>(null);

  const groupsQuery = useQuery({
    queryKey: ["user-groups"],
    queryFn: () => api.listUserGroups(),
    enabled: auth.status === "authed",
  });

  const createMutation = useMutation({
    mutationFn: (input: { name: string; uids: number[] }) =>
      api.createUserGroup(input),
    onSuccess: async () => {
      setName("");
      setUidsText("");
      setFormError(null);
      toast.add({
        title: "用户组已创建",
        description: "可在发布时选择该组做定向发布。",
        type: "success",
      });
      await queryClient.invalidateQueries({ queryKey: ["user-groups"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "name_taken") {
        setFormError("该名称已存在。");
      } else if (err instanceof ApiError && err.code === "invalid_name") {
        setFormError("请输入有效的名称。");
      } else if (err instanceof ApiError && err.code === "invalid_uid") {
        setFormError("UID 必须是正整数。");
      } else {
        setFormError("无法创建用户组，请重试。");
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (groupId: string) => api.deleteUserGroup(groupId),
    onSuccess: async () => {
      toast.add({
        title: "用户组已删除",
        type: "success",
      });
      await queryClient.invalidateQueries({ queryKey: ["user-groups"] });
    },
    onError: () => {
      toast.add({
        title: "删除失败",
        description: "无法删除用户组。",
        type: "error",
      });
    },
  });

  if (auth.status === "loading") {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-4 w-96" />
        </div>
        <GroupsTableSkeleton />
      </div>
    );
  }

  if (auth.status === "anon") return null;

  const groups = groupsQuery.data?.groups ?? [];

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (createMutation.isPending) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setFormError("请输入名称。");
      return;
    }
    const parsed = parseUidTextClient(uidsText);
    if (!parsed.ok) {
      setFormError(`无效 UID：${parsed.badToken}`);
      return;
    }
    setFormError(null);
    createMutation.mutate({ name: trimmed, uids: parsed.uids });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="font-heading text-2xl font-semibold tracking-tight">
          用户组
        </h1>
        <p className="text-sm text-muted-foreground">
          管理玩家 UID 分组。发布时默认全量；也可多选用户组做定向发布。
        </p>
      </div>

      <form
        onSubmit={onCreate}
        className="rounded-lg border border-border p-4"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="group-name">名称</FieldLabel>
            <Input
              id="group-name"
              autoComplete="off"
              required
              placeholder="例如：内测用户"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="sm:max-w-sm"
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="group-uids">UID（可选）</FieldLabel>
            <Textarea
              id="group-uids"
              placeholder={"每行一个，或用逗号/空格分隔\n例如：1001, 1002"}
              value={uidsText}
              onChange={(e) => setUidsText(e.target.value)}
              className="min-h-24 sm:max-w-lg"
            />
            <FieldDescription>
              玩家 UID 为正整数。可先建空组，稍后在编辑里添加。
            </FieldDescription>
          </Field>
          <div>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <Spinner />
              ) : (
                <HugeiconsIcon icon={Add01Icon} data-icon="inline-start" />
              )}
              创建用户组
            </Button>
          </div>
        </FieldGroup>
        {formError ? (
          <p className="mt-3 text-sm text-destructive" role="alert">
            {formError}
          </p>
        ) : null}
      </form>

      {groupsQuery.isLoading && <GroupsTableSkeleton />}

      {groupsQuery.isError && (
        <p className="text-sm text-destructive" role="alert">
          无法加载用户组，请刷新重试。
        </p>
      )}

      {groupsQuery.isSuccess && groups.length === 0 && (
        <Empty className="border border-border">
          <EmptyHeader>
            <EmptyTitle>暂无用户组</EmptyTitle>
            <EmptyDescription>
              创建用户组后，可在案件发布时选择定向发布范围。
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}

      {groupsQuery.isSuccess && groups.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>名称</TableHead>
                <TableHead>UID 数</TableHead>
                <TableHead>更新时间</TableHead>
                <TableHead className="w-40">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map((group) => (
                <TableRow key={group.id}>
                  <TableCell className="font-medium">{group.name}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{group.uidCount}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatWhen(group.updatedAt)}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEditGroup(group)}
                      >
                        <HugeiconsIcon
                          icon={Edit02Icon}
                          data-icon="inline-start"
                        />
                        编辑
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger
                          render={
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={deleteMutation.isPending}
                            />
                          }
                        >
                          <HugeiconsIcon
                            icon={Delete02Icon}
                            data-icon="inline-start"
                          />
                          删除
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              删除 {group.name}？
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              删除后发布页将无法再选择该组。不影响已发布记录。
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>取消</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteMutation.mutate(group.id)}
                              disabled={deleteMutation.isPending}
                            >
                              删除用户组
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {editGroup ? (
        <EditUserGroupDialog
          group={editGroup}
          open={Boolean(editGroup)}
          onOpenChange={(open) => {
            if (!open) setEditGroup(null);
          }}
          onUpdated={async (group) => {
            setEditGroup(group);
            await queryClient.invalidateQueries({ queryKey: ["user-groups"] });
          }}
        />
      ) : null}
    </div>
  );
}

function EditUserGroupDialog({
  group,
  open,
  onOpenChange,
  onUpdated,
}: {
  group: UserGroup;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: (group: UserGroup) => void | Promise<void>;
}) {
  const [name, setName] = useState(group.name);
  const [addText, setAddText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const sortedUids = useMemo(
    () => [...group.uids].sort((a, b) => a - b),
    [group.uids],
  );

  const renameMutation = useMutation({
    mutationFn: (nextName: string) => api.renameUserGroup(group.id, nextName),
    onSuccess: async (result) => {
      setError(null);
      toast.add({ title: "名称已更新", type: "success" });
      await onUpdated(result.group);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "name_taken") {
        setError("该名称已存在。");
      } else {
        setError("无法重命名。");
      }
    },
  });

  const addMutation = useMutation({
    mutationFn: (uids: number[]) => api.addUserGroupUids(group.id, uids),
    onSuccess: async (result) => {
      setAddText("");
      setError(null);
      toast.add({
        title: "UID 已添加",
        description: `当前共 ${result.group.uidCount} 个。`,
        type: "success",
      });
      await onUpdated(result.group);
    },
    onError: () => setError("无法添加 UID。"),
  });

  const removeMutation = useMutation({
    mutationFn: (uid: number) => api.removeUserGroupUids(group.id, [uid]),
    onSuccess: async (result) => {
      toast.add({ title: "UID 已移除", type: "success" });
      await onUpdated(result.group);
    },
    onError: () => {
      toast.add({ title: "移除失败", type: "error" });
    },
  });

  const busy =
    renameMutation.isPending ||
    addMutation.isPending ||
    removeMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>编辑用户组</DialogTitle>
          <DialogDescription>
            修改名称或增删玩家 UID。发布时多选用户组会合并 UID。
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <Field>
            <FieldLabel htmlFor="edit-group-name">名称</FieldLabel>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="edit-group-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
              />
              <Button
                type="button"
                variant="outline"
                disabled={busy || name.trim() === group.name}
                onClick={() => {
                  const next = name.trim();
                  if (!next) {
                    setError("名称不能为空。");
                    return;
                  }
                  renameMutation.mutate(next);
                }}
              >
                {renameMutation.isPending ? <Spinner /> : null}
                保存名称
              </Button>
            </div>
          </Field>

          <Field>
            <FieldLabel htmlFor="edit-add-uids">添加 UID</FieldLabel>
            <Textarea
              id="edit-add-uids"
              placeholder="粘贴多个 UID，逗号或换行分隔"
              value={addText}
              onChange={(e) => setAddText(e.target.value)}
              disabled={busy}
              className="min-h-20"
            />
            <div className="mt-2">
              <Button
                type="button"
                disabled={busy || !addText.trim()}
                onClick={() => {
                  const parsed = parseUidTextClient(addText);
                  if (!parsed.ok) {
                    setError(`无效 UID：${parsed.badToken}`);
                    return;
                  }
                  if (parsed.uids.length === 0) {
                    setError("请输入至少一个 UID。");
                    return;
                  }
                  setError(null);
                  addMutation.mutate(parsed.uids);
                }}
              >
                {addMutation.isPending ? <Spinner /> : null}
                添加
              </Button>
            </div>
          </Field>

          <div className="flex flex-col gap-2">
            <FieldLabel>当前 UID（{sortedUids.length}）</FieldLabel>
            {sortedUids.length === 0 ? (
              <p className="text-sm text-muted-foreground">暂无 UID。</p>
            ) : (
              <div className="max-h-56 overflow-y-auto rounded-lg border border-border">
                <Table>
                  <TableBody>
                    {sortedUids.map((uid) => (
                      <TableRow key={uid}>
                        <TableCell className="font-mono text-sm">
                          {uid}
                        </TableCell>
                        <TableCell className="w-24 text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={busy}
                            onClick={() => removeMutation.mutate(uid)}
                          >
                            移除
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            关闭
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
